// Ingredient identity: reduces a free-text food name to a comparison key so
// the same food written two ways ("Carrots", "carrot", "medium carrots,
// diced") compares equal. Design: docs/2026-09-20-inventory-design.md, 3.2.
//
// The key is for COMPARING only, never for display -- it is not a valid word
// ("molasses" becomes "molass", "cookies" becomes "cooky") and only has to be
// the same for every spelling of one food.
//
// Deliberately conservative. A false negative (two spellings of one food not
// matching) costs a duplicate purchase; a false positive (two different foods
// matching) hides something needed on the grocery list. So only changes that
// cannot turn one food into another are made: case, accents, punctuation,
// parentheticals, everything after the first comma (prep), a short list of
// size/marketing words, and plural endings on the last word. No fuzzy or
// edit-distance matching, no synonyms, no dropping of words like "fresh",
// "dried", "whole" or "ground" (they usually name a different product).
//
// Pure function with no imports. A hand-synced TypeScript copy lives at
// supabase/functions/import-recipe-from-url/itemIdentity.ts (Edge Functions
// cannot import from src/); tests/item-identity.test.mjs fails if the two
// drift apart.

// Words that describe the specific item bought, not what food it is.
const DROPPED_WORDS = new Set(["small", "medium", "large", "big", "jumbo", "organic"]);

// Plurals that the general rules below would get wrong.
const IRREGULAR_PLURALS = { leaves: "leaf", loaves: "loaf", halves: "half" };

// Spellings of one food that the general rules above can't unify. Deliberately
// a short, explicit list -- not fuzzy matching -- so each entry is a decision
// that two spellings are the same product (from a scan of the household's own
// ingredient names, 2026-09-27). Applied to the whole cleaned name.
// NOT here on purpose: "chile" vs "chili" (chili powder is a spice blend, chile
// powder is pure ground chile), and a bare "creme" (creme fraiche is not cream).
const SPELLING_VARIANTS = [
  [/\bchilli\b/g, "chili"],
  [/\bchillies\b/g, "chilies"],
  [/\bcorn starch\b/g, "cornstarch"],
  [/\bbread crumbs?\b/g, "breadcrumbs"],
  [/\bchick peas?\b/g, "chickpeas"],
  [/\bsemi sweet\b/g, "semisweet"],
  [/\bmarshmallow creme\b/g, "marshmallow cream"],
];

// Applied to the last word of the name only ("chicken breasts" -> "chicken
// breast"). Words that are already singular are left alone, and the rules are
// idempotent so normalising a key again returns the same key.
function singularize(word) {
  if (word.length < 4) return word;
  if (IRREGULAR_PLURALS[word]) return IRREGULAR_PLURALS[word];
  // berries -> berry, and the singular "-ie" (cookie, veggie) is folded the
  // same way so cookie and cookies still agree. Length guards leave pie/pies
  // alone (a false negative, which is the safe direction).
  if (word.endsWith("ies") && word.length > 4) return word.slice(0, -3) + "y";
  if (word.endsWith("ie") && word.length >= 5) return word.slice(0, -2) + "y";
  // tomatoes -> tomato, radishes -> radish, peaches -> peach. Not "ses"
  // (cheeses -> cheese is handled by the generic rule below).
  if (/(oes|ches|shes|xes|zes|sses)$/.test(word)) return word.slice(0, -2);
  // carrots -> carrot. Leaves hummus, asparagus, couscous, swiss ("us",
  // "is", "ss" endings) alone.
  if (word.endsWith("s") && !/(ss|us|is)$/.test(word)) return word.slice(0, -1);
  return word;
}

export function normalizeIngredientName(name) {
  if (name == null) return "";
  let s = String(name).toLowerCase().normalize("NFD").replace(/\p{M}/gu, "");

  // Parentheticals ("cooking oil (15ml)", "(8 oz) flour"), innermost first;
  // then any unbalanced "(" fragment.
  let previous;
  do {
    previous = s;
    s = s.replace(/\([^()]*\)/g, " ");
  } while (s !== previous);
  s = s.replace(/\(.*$/, " ").replace(/[()]/g, " ");

  // Everything after the first comma or semicolon is prep or an aside
  // ("yellow onion, coarsely chopped").
  s = s.split(/[,;]/)[0];

  // Apostrophes vanish (confectioners' -> confectioners); other punctuation
  // separates words (extra-virgin -> extra virgin, half-and-half).
  s = s.replace(/['‘’`]/g, "").replace(/[^\p{L}\p{N}]+/gu, " ").trim();

  s = s.replace(/\bextra large\b/g, " ");
  const kept = s.split(/\s+/).filter((w) => w && !DROPPED_WORDS.has(w));
  if (kept.length === 0) return "";
  let joined = kept.join(" ");
  for (const [pattern, replacement] of SPELLING_VARIANTS) joined = joined.replace(pattern, replacement);
  const words = joined.split(" ");
  // A bare "pepper" is the spice and a bare "peppers" is the vegetable, so
  // that one plural must not fold ("bell peppers" -> "bell pepper" still does).
  if (words.length === 1 && words[0] === "peppers") return "peppers";
  words[words.length - 1] = singularize(words[words.length - 1]);
  return words.join(" ");
}

export function isSameIngredient(a, b) {
  const keyA = normalizeIngredientName(a);
  return keyA !== "" && keyA === normalizeIngredientName(b);
}
